import { useEffect, useState } from "react";
import {
  Link,
  useParams,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import styled from "styled-components";
import { DashboardLayout } from "src/components/dashboard";
import { ProfileInfo } from "src/components/common";
import { BookingsTabs, BookingStatus } from "src/components/bookings";
import arrowLeft from "src/assets/images/common/arrowLeft.svg";
import { theme } from "src/styles/Theme";
import { Flex, Button, ButtonClass } from "src/components/ui";
import { formatDateDmy, numberWithCommas } from "src/utils/helpers";
import { useGetUserQuery } from "src/store/services/userApi";
import { useUserBookingHistoryQuery } from "src/store/services/bookingApi";
import { ToastContainer } from "react-toastify";
import avatar from "../../assets/images/header/avatar.svg";
import bookingsIcon from "src/assets/images/metrics/bookingSummary.svg";
import walletIcon from "src/assets/images/metrics/walletIcon.svg";
import WalletTable from "src/components/artisan/WalletTable";
import ReferredUserTable from "src/components/artisan/referredUserTables";
import { ScaleLoader } from "react-spinners";
import { BookPaginationProps } from "src/components/ui/Pagination";

const LIMIT = 10;

const UserProfileContainer = styled.div`
  .loader-container {
    border-radius: 16px;
    background: #ffffff;
    height: 300px;
    margin-top: 36px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const StyledBookingSummary = styled.div`
  margin: 32px 0px;
  display: flex;
  gap: 36px;
  .booking_summary,
  .wallet_summary {
    background: #ffffff;
    border-radius: 16px;
    padding: 24px 24px 33px 41px;
    display: flex;
    gap: 28px;
    .summary_details {
      display: flex;
      flex-direction: column;
      gap: 12px;
      h5 {
        font-size: 16px;
        font-weight: 500;
      }
      h3 {
        font-size: 36px;
      }
    }
  }
`;

export const RhsHeading = () => (
  <Flex wrap="wrap">
    <Link to="/customers">
      <Button
        classes={[ButtonClass.SOLID, ButtonClass.WITH_ICON]}
        style={{ backgroundColor: theme.colors.purple }}
      >
        {" "}
        <img src={arrowLeft} alt="back" />
        <span>Back to Customers</span>
      </Button>
    </Link>
  </Flex>
);

const UserProfile = () => {
  const navigate = useNavigate();
  const [pageNo, setPageNo] = useState(1);

  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date;
  });
  const [status, setStatus] = useState("");
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [paginationData, setPaginationData] = useState<BookPaginationProps>({
    page: 0,
    pageSize: 0,
    total: 0,
    totalPages: 0,
    totalBookings: 0,
  });
  const { id } = useParams();
  const [userDetails, setUserDetails] = useState<{ [key: string]: any }>({});
  const [userWalletDetails, setUserWalletDetails] = useState<WalletDataTypes>({
    balance: 0,
    transactions: [],
  });
  const [userBookings, setUserBookings] = useState<BookingsTypes[]>([]);

  // RTK: fetch user profile
  const { data: userResp, isLoading: isLoadingUser } = useGetUserQuery(
    id as string,
    { skip: !id },
  );
  useEffect(() => {
    const payload: any = userResp?.payload?.data;
    if (!payload) return;
    setUserDetails(payload);
    setUserWalletDetails(payload?.wallet || { balance: 0, transactions: [] });
  }, [userResp]);

  // RTK: fetch user bookings — page/limit are query args, so RTK Query
  // automatically refetches whenever pageNo changes.
  // NOTE: status/startDate/endDate are NOT sent to the API here — the
  // current endpoint type only accepts userId/page/limit. If/when the
  // backend + service definition support filtering, add them back into
  // this args object and into bookingApi.ts's query signature.
  const { data: bookingsResp } = useUserBookingHistoryQuery(
    {
      userId: id!,
      page: pageNo,
      limit: LIMIT,
      status: status,
    },
    { skip: !id },
  );

  useEffect(() => {
    const rows: any[] = bookingsResp?.payload?.data || [];
    setUserBookings(rows as any);

    const pagination = bookingsResp?.payload?.pagination;
    if (pagination) {
      setPaginationData({
        page: pagination.page,
        pageSize: pagination.limit,
        total: pagination.total,
        totalPages: pagination.totalPages,
        totalBookings: pagination.totalBookings,
      });
    }
  }, [bookingsResp]);

  // Reset to page 1 whenever filters change, so the user doesn't land on
  // an out-of-range page for the newly filtered result set
  useEffect(() => {
    setPageNo(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, startDate, endDate]);

  let [searchParams, setSearchParams] = useSearchParams();

  const handleNavigate = (row: any) => {
    navigate(`/bookings/${row?._id}?tabStatus=${row?.status}`);
  };

  const BookingsTableHeaders = [
    {
      title: "Vendor/Partner",
      render: (row: BookingsTypes) => (
        <Flex gap="10px" align="center">
          <img
            style={{ width: "40px", height: 40, borderRadius: "50%" }}
            src={row?.artisan_meta?.display_picture || avatar}
            alt=""
          />{" "}
          {row?.booking_type === "partner"
            ? row?.artisan_meta?.business_name || ""
            : `${row?.artisan_meta?.first_name || ""} ${row?.artisan_meta?.last_name || ""}`}
        </Flex>
      ),
    },
    {
      title: "Category",
      render: (row: BookingsTypes) =>
        `${
          row?.booking_type === "partner"
            ? row?.category?.name || ""
            : `${row?.service}`
        }`,
    },
    {
      title: "Location",
      render: (row: BookingsTypes) =>
        `${
          row?.booking_type === "partner"
            ? row?.delivery_info?.delivery_type === "pickup"
              ? row?.artisan_meta?.shop_address || ""
              : row?.user_meta?.address?.street || ""
            : row?.artisan_meta?.address?.street || ""
        }`,
    },
    {
      title: "Date",
      render: (row: BookingsTypes) => formatDateDmy(row?.createdAt || ""),
    },
    {
      title: "Status",
      render: (row: BookingsTypes) => (
        <BookingStatus status={row["status"] || ""} />
      ),
    },
  ];

  return (
    <DashboardLayout
      pageTitle={
        userDetails?.user?.first_name &&
        `${userDetails?.user?.first_name} ${userDetails?.user?.last_name}’s Profile`
      }
      rhsHeading={<RhsHeading />}
    >
      <ToastContainer />
      <UserProfileContainer>
        {isLoadingUser ? (
          <div className="loader-container">
            <ScaleLoader color="#7E00C4" height={50} width={8} />
          </div>
        ) : (
          <ProfileInfo userDetails={userDetails} />
        )}
        <StyledBookingSummary>
          <div className="booking_summary">
            <div className="summary_details">
              <h5>Total Bookings</h5>{" "}
              <h3>
                {paginationData?.totalBookings ?? userBookings?.length ?? 0}
              </h3>
            </div>
            <img src={bookingsIcon} alt="" width={55} height="55px" />
          </div>
          <div className="wallet_summary">
            <div className="summary_details">
              <h5>Wallet Balance</h5>{" "}
              <h3>
                {userDetails?.user?.currency_symbol || "₦"}
                {numberWithCommas(userWalletDetails?.balance) || 0}
              </h3>
            </div>
            <img src={walletIcon} alt="" width={55} height="55px" />
          </div>
        </StyledBookingSummary>
        <BookingsTabs
          rows={userBookings}
          BookingsTableHeaders={BookingsTableHeaders}
          title={<h1 className="title">Bookings</h1>}
          onRowClick={handleNavigate}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          paginationData={paginationData}
          pgn={pageNo}
          setpgn={setPageNo}
          setStatus={setStatus}
          setEndDate={setEndDate}
          setStartDate={setStartDate}
        />
        <WalletTable
          currency={userDetails?.user?.currency_symbol || "₦"}
          rows={userWalletDetails?.transactions}
        />
        <ReferredUserTable rows={userDetails?.referredUsers || []} />
      </UserProfileContainer>
    </DashboardLayout>
  );
};

export default UserProfile;
